import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Copy, Link, Play, Users } from 'lucide-react';
import { LiveBadge } from '@components/live/LiveBadge';
import { POLL_COPY } from '@components/polls/pollCopy';
import { pollLeaderIds, pollShare } from '@components/polls/pollFormat';
import { canGoBack, forwardMove } from '@components/livePolls/bigScreen/BigScreenAuthorBar';
import { RESULTS_PAGE_SIZE, shownResultsPage } from '@lib/livePolls/resultsPaging';
import { useLiveMoves, type LiveMoves } from '@lib/livePolls/useLiveMoves';
import { cn } from '@lib/utils';
import type { LiveHostState, LiveQuestion, LiveState } from '@models/api';
import { EndLivePollDrawer } from './EndLivePollDrawer';
import { LIVE_HOST_COPY, LIVE_POLL_COPY } from './livePollCopy';
import { copyScreenLink } from './screenLinkActions';

export type HostControlsSize = 'compact' | 'large';

export interface HostControlsProps {
  code: string;
  host: LiveHostState;
  /** `compact`: the author's card drawer (L9b–L12); `large`: the host view (H2–H4). RESOLUTIONS L26. */
  size: HostControlsSize;
  /** The host view shares one `useLiveMoves` with its own buttons (Start, Show question 1). */
  moves?: LiveMoves;
}

/**
 * A press within this long of the last one is a double tap, not a second move. On a fast network a move can
 * land, and the controls show the new question, before a double tap's second press: `expected_position` then
 * matches and the poll would skip a question (TECH-867 § Errors "pressed twice"; browser check: two clicks
 * 30 ms apart move once).
 */
export const DOUBLE_TAP_MS = 500;

/** `moves` with a double tap counted once (see `DOUBLE_TAP_MS`); the in-flight hold still applies. */
// eslint-disable-next-line react-refresh/only-export-components -- the host view's own buttons share it
export function useDoubleTapGuard(moves: LiveMoves): LiveMoves {
  const held = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );
  const hold = useCallback((press: () => boolean): boolean => {
    if (held.current) return false;
    const sent = press();
    if (sent) {
      held.current = true;
      timer.current = setTimeout(() => {
        held.current = false;
      }, DOUBLE_TAP_MS);
    }
    return sent;
  }, []);
  const { move: rawMove, page: rawPage, pending } = moves;
  const move = useCallback<LiveMoves['move']>(
    (kind, from) => hold(() => rawMove(kind, from)),
    [hold, rawMove],
  );
  const page = useCallback<LiveMoves['page']>(
    action => hold(() => rawPage(action)),
    [hold, rawPage],
  );
  return { move, page, pending };
}

/** Which side of the run the controls are on: a question open, or the results. */
type Phase = 'question' | 'results';

const phaseOf = (state: LiveState): Phase | null => {
  if (state.live_state === 'ended' || state.removed) return 'results';
  if (state.live_state === 'live' && state.current_position !== null && state.question)
    return 'question';
  return null;
};

/** The top answers of a shown question, every tied one (founder, 3 Nov), with their shares; none if nobody answered. */
const topAnswers = (question: LiveQuestion): { id: string; text: string; share: number }[] => {
  const leaders = pollLeaderIds(question);
  return [...question.options]
    .filter(option => leaders.has(option.id))
    .sort((a, b) => a.position - b.position)
    .map(option => ({
      id: option.id,
      text: option.text,
      share: pollShare(option.votes_count, question.voters_count),
    }));
};

/** The page of results the screen shows now (CONTRACT PR2), re-read every second while it rotates. */
const useShownPage = (state: LiveState, shown: number) => {
  const [now, setNow] = useState(() => Date.now());
  const rotating = shown > RESULTS_PAGE_SIZE && state.results_paging?.paused !== true;
  useEffect(() => {
    if (!rotating) return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [rotating]);
  return shownResultsPage(state.results_paging, shown, now, state.closed_at);
};

/** Progress segments: done and current in teal-ink, the rest on the track (L9b). */

export const Progress: React.FC<{ count: number; upTo: number }> = ({ count, upTo }) => (
  // Segments 18px wide, narrowing (to 4px) so ten of them never push "Question {n} of {m}" onto two lines.
  <span className="flex min-w-0 justify-end gap-1" aria-hidden="true" data-testid="host-progress">
    {Array.from({ length: count }, (_, i) => (
      <span
        key={i}
        className={cn(
          'h-1.5 w-[18px] min-w-1 shrink rounded-full',
          i <= upTo ? 'bg-mb-teal-ink' : 'bg-mb-progress-track',
        )}
      />
    ))}
  </span>
);

/** One option's live bar: "{option}" and "{share}% · {count}" (L9b compact h36, H2 large h52). */
const ResultRow: React.FC<{ text: string; share: number; count: number; large: boolean }> = ({
  text,
  share,
  count,
  large,
}) => (
  <li
    className={cn(
      'relative flex items-center gap-3 overflow-hidden border border-mb-border bg-white',
      large ? 'min-h-[52px] rounded-xl px-3.5' : 'min-h-9 gap-2 rounded-[10px] px-3',
    )}
  >
    <span
      aria-hidden="true"
      className="absolute inset-y-0 left-0 bg-mb-teal-light transition-[width] duration-300 motion-reduce:transition-none"
      style={{ width: `${Math.min(100, Math.max(0, share))}%` }}
    />
    <span
      className={cn(
        'relative min-w-0 flex-1 py-1.5 break-words text-mb-ink',
        large ? 'text-base leading-5' : 'text-sm leading-[18px]',
      )}
    >
      {text}
    </span>
    <span
      className={cn(
        'relative shrink-0 font-medium text-mb-gray-dark',
        large ? 'text-sm leading-[18px]' : 'text-[13px] leading-4',
      )}
    >
      {LIVE_HOST_COPY.shareAndCount(share, count)}
    </span>
  </li>
);

/** "Copy screen link": a text link with a 44px hit area (L8). */
const CopyScreenLink: React.FC<{ code: string; host: LiveHostState; large: boolean }> = ({
  code,
  host,
  large,
}) => (
  <button
    type="button"
    onClick={() => {
      void copyScreenLink(code, host.presenter_key);
    }}
    className={cn(
      'inline-flex min-h-11 items-center gap-1.5 font-semibold text-mb-teal-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink',
      large ? 'text-sm leading-[18px]' : 'text-[13px] leading-4 font-medium',
    )}
  >
    {large ? (
      <Copy className="size-4" strokeWidth={2} aria-hidden="true" />
    ) : (
      <Link className="size-3.5" strokeWidth={2} aria-hidden="true" />
    )}
    {LIVE_HOST_COPY.copyScreenLink}
  </button>
);

/**
 * The host controls (TECH-867 L10–L12 in the card's drawer, H2–H4 in the host view): one component with a size
 * (RESOLUTIONS L26). While a question is open: "Question {n} of {m}", the live bars "{share}% · {count}", "{n}
 * answers", Back (from question 2), "Next question" (yellow) or "Show results" on the last question (that is the
 * End), "Copy screen link" and "End poll" (red outline, the one End confirm). After the results: the top answers,
 * "Back to question {n}" only when `can_back_from_results` (an admin's End or an archived poll hides it), "Copy
 * screen link", and in the host view the screen's paging with more than 6 questions shown.
 *
 * Every press goes through `useLiveMoves`: it sends `expected_position` and `expected_live_state`, holds every
 * control while one is in flight (a double tap sends once), and on a 409 shows the fresh `details.state`.
 */
export function HostControls({ code, host, size, moves }: HostControlsProps) {
  const own = useLiveMoves(code);
  const { move, page, pending } = useDoubleTapGuard(moves ?? own);
  // The confirm belongs to the question it was opened on: a move elsewhere closes it for good (never reopens).
  const [endOpenOn, setEndOpenOn] = useState<string | null>(null);
  const { state } = host;
  const at = `${state.live_state}:${String(state.current_position)}`;
  // A move (here or on another device) drops a confirm opened before it, so Back onto that question never
  // reopens it (the FE-L2b review's bug): React's "adjust state when a prop changes" pattern.
  const [seenAt, setSeenAt] = useState(at);
  if (seenAt !== at) {
    setSeenAt(at);
    setEndOpenOn(null);
  }
  const phase = phaseOf(state);
  const large = size === 'large';
  const busy = pending !== null;

  if (phase === null) return null;

  const button = cn(
    'inline-flex shrink-0 items-center justify-center gap-1 rounded-full font-semibold text-mb-ink transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink disabled:cursor-progress disabled:opacity-60',
    large ? 'h-[52px] text-base leading-5' : 'h-11 text-sm leading-[18px] font-medium',
  );
  const chevron = large ? 'size-5' : 'size-4';

  if (phase === 'results') {
    return (
      <ResultsControls
        code={code}
        host={host}
        large={large}
        busy={busy}
        button={button}
        chevron={chevron}
        onBack={() => {
          move('back', state);
        }}
        onPage={action => {
          page(action);
        }}
      />
    );
  }

  const question = state.question;
  if (!question) return null;
  const position = state.current_position ?? 0;
  const forward = forwardMove(state);
  const last = forward === 'end';
  const n = position + 1;
  const m = state.questions_count;
  const eyebrow =
    large && last ? LIVE_HOST_COPY.questionOfLast(n, m) : LIVE_HOST_COPY.questionOf(n, m);

  const body = (
    <>
      {large ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm leading-[18px] font-semibold text-mb-dark-gray">{eyebrow}</p>
          <h2 className="font-livvic text-[26px] leading-8 font-bold break-words text-mb-ink">
            {question.text}
          </h2>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className="flex shrink-0 items-center gap-2">
              <LiveBadge />
              <span className="text-sm leading-[18px] font-semibold whitespace-nowrap text-mb-ink">
                {eyebrow}
              </span>
            </span>
            <Progress count={m} upTo={position} />
          </div>
          <p className="text-base leading-5 font-semibold break-words text-mb-ink">
            {question.text}
          </p>
        </>
      )}
      <ul className={cn('flex flex-col', large ? 'gap-2.5' : 'gap-1.5')}>
        {question.options.map(option => (
          <ResultRow
            key={option.id}
            text={option.text}
            share={pollShare(option.votes_count, question.voters_count)}
            count={option.votes_count}
            large={large}
          />
        ))}
      </ul>
      {large ? (
        <p className="font-livvic text-[28px] leading-8 font-bold text-mb-ink">
          {LIVE_POLL_COPY.answers(question.voters_count)}
        </p>
      ) : (
        <p className="flex items-center gap-1.5 text-xs leading-4 font-medium text-mb-dark-gray">
          <Users className="size-3.5" aria-hidden="true" />
          {LIVE_POLL_COPY.answers(question.voters_count)}
        </p>
      )}
    </>
  );

  const actions = (
    <>
      <div className={cn('flex', large ? 'gap-2.5' : 'gap-2')}>
        {canGoBack(state) ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              move('back', state);
            }}
            className={cn(
              button,
              'bg-white hover:bg-mb-bg',
              large
                ? 'w-[110px] border-[1.5px] border-mb-control-border'
                : 'border border-mb-control-border px-4',
            )}
          >
            <ChevronLeft className={chevron} strokeWidth={2} aria-hidden="true" />
            {LIVE_POLL_COPY.back}
          </button>
        ) : null}
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            if (forward) move(forward, state);
          }}
          className={cn(
            button,
            'grow bg-mb-yellow px-4 hover:bg-mb-yellow-dark',
            !large && 'shadow-[0_4px_5px_#FFCA2880]',
          )}
          data-testid="host-forward"
        >
          {last ? LIVE_POLL_COPY.showResults : LIVE_POLL_COPY.nextQuestion}
          {last && large ? null : (
            <ChevronRight className={chevron} strokeWidth={2} aria-hidden="true" />
          )}
        </button>
      </div>
      <div
        className={cn(
          'flex items-center justify-between gap-3',
          !large && 'border-t border-mb-border pt-2.5',
        )}
      >
        <CopyScreenLink code={code} host={host} large={large} />
        {last ? null : (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setEndOpenOn(at);
            }}
            className={cn(
              // mb-error-text #B91C1C (no token in tailwind.css).
              'inline-flex h-11 shrink-0 items-center rounded-full px-[18px] font-semibold text-[#B91C1C] hover:bg-red-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink disabled:cursor-progress disabled:opacity-60',
              large
                ? 'border-[1.5px] border-[#B91C1C] text-[15px] leading-5'
                : 'border border-[#B91C1C] text-[13px] leading-4',
            )}
          >
            {LIVE_POLL_COPY.endPoll}
          </button>
        )}
      </div>
      {large ? null : (
        <p className="text-xs leading-4 text-mb-dark-gray">{LIVE_HOST_COPY.onlyYouSeeControls}</p>
      )}
    </>
  );

  const confirm = (
    <EndLivePollDrawer
      open={endOpenOn === at}
      onOpenChange={next => {
        setEndOpenOn(next ? at : null);
      }}
      noQuestionsRun={false}
      isConfirming={pending === 'end'}
      onConfirm={() => {
        move('end', state);
        setEndOpenOn(null);
      }}
    />
  );

  if (!large) {
    return (
      <div className="flex flex-col gap-3" data-testid="host-controls" data-size={size}>
        {body}
        {actions}
        {confirm}
      </div>
    );
  }
  return (
    <HostLayout body={body} bar={actions} testId="host-controls" size={size}>
      {confirm}
    </HostLayout>
  );
}

/**
 * The host view's two parts (H1–H4): the scrolling body, and the white bar pinned at the bottom (padding
 * 16/16/28, gap 14) that holds the controls. Shared with the host view's own H1/H1b/H7 states.
 */
export function HostLayout({
  body,
  bar,
  testId,
  size,
  barClassName,
  bodyClassName,
  children,
}: {
  body: React.ReactNode;
  bar: React.ReactNode;
  testId?: string;
  size?: HostControlsSize;
  barClassName?: string;
  /** The body's gap when a frame differs from H1–H3's 20 (H4: 18). */
  bodyClassName?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid={testId} data-size={size}>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className={cn('flex flex-col gap-5 px-4 py-6', bodyClassName)}>{body}</div>
      </div>
      <div
        className={cn(
          'flex shrink-0 flex-col gap-3.5 border-t border-mb-border bg-white px-4 pt-4 pb-[max(28px,env(safe-area-inset-bottom))]',
          barClassName,
        )}
      >
        {bar}
      </div>
      {children}
    </div>
  );
}

const ResultsControls: React.FC<{
  code: string;
  host: LiveHostState;
  large: boolean;
  busy: boolean;
  button: string;
  chevron: string;
  onBack: () => void;
  onPage: (action: 'pause' | 'resume' | 'next') => void;
}> = ({ code, host, large, busy, button, chevron, onBack, onPage }) => {
  const { state } = host;
  const results = state.results ?? [];
  const shown = useShownPage(state, results.length);
  const lastShown = (state.current_position ?? results.length - 1) + 1;
  const canBack = host.can_back_from_results && !state.removed && results.length > 0;
  const paging = large && results.length > RESULTS_PAGE_SIZE;
  const onPage0 = paging
    ? results.slice(shown.page * RESULTS_PAGE_SIZE, (shown.page + 1) * RESULTS_PAGE_SIZE)
    : results;

  const back = canBack ? (
    <button
      type="button"
      disabled={busy}
      onClick={onBack}
      className={cn(
        button,
        'w-full bg-white hover:bg-mb-bg',
        large ? 'border-[1.5px] border-mb-control-border' : 'border border-mb-control-border px-4',
      )}
    >
      <ChevronLeft className={chevron} strokeWidth={2} aria-hidden="true" />
      {LIVE_HOST_COPY.backToQuestion(lastShown)}
    </button>
  ) : null;

  if (!large) {
    return (
      <div className="flex flex-col gap-3" data-testid="host-controls" data-size="compact">
        <div className="flex items-center justify-between gap-2">
          <span className="flex shrink-0 items-center gap-2">
            <EndedChip />
            <span className="text-sm leading-[18px] font-semibold text-mb-ink">
              {LIVE_HOST_COPY.resultsOnTheScreen}
            </span>
          </span>
          <Progress count={state.questions_count} upTo={state.questions_count} />
        </div>
        <p className="text-base leading-5 font-semibold text-mb-ink">{LIVE_HOST_COPY.topAnswers}</p>
        <ul className="flex flex-col gap-2">
          {results.map(question => {
            const tops = topAnswers(question);
            return (
              <li
                key={question.position}
                className="flex justify-between gap-2 text-sm leading-[18px]"
              >
                <span className="min-w-0 flex-1 break-words text-mb-gray-dark">
                  {LIVE_POLL_COPY.numberedQuestion(question.position + 1, question.text)}
                </span>
                {tops.length > 0 ? (
                  <span className="flex max-w-[150px] flex-col gap-1 text-right font-semibold break-words text-mb-teal-deep">
                    {tops.map(top => (
                      <span key={top.id}>{LIVE_HOST_COPY.topAnswerShare(top.text, top.share)}</span>
                    ))}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
        <p className="flex items-center gap-1.5 text-xs leading-4 font-medium text-mb-dark-gray">
          <Users className="size-3.5" aria-hidden="true" />
          {POLL_COPY.peopleAnswered(state.voters_count ?? 0)}
        </p>
        {back ? <div className="flex">{back}</div> : null}
        <div className="flex items-center border-t border-mb-border pt-2.5">
          <CopyScreenLink code={code} host={host} large={false} />
        </div>
        {canBack ? (
          <p className="text-xs leading-4 text-mb-dark-gray">
            {LIVE_HOST_COPY.backReopens(lastShown)}
          </p>
        ) : null}
      </div>
    );
  }

  const body = (
    <>
      <div className="flex flex-col gap-1.5">
        <p className="text-sm leading-[18px] font-semibold text-mb-dark-gray">
          {LIVE_HOST_COPY.endedEyebrow}
        </p>
        <h2 className="font-livvic text-[28px] leading-[34px] font-bold text-mb-ink">
          {LIVE_HOST_COPY.results}
        </h2>
      </div>
      <ul className="flex flex-col gap-3" data-testid="host-results">
        {onPage0.map(question => (
          <li key={question.position} className="flex flex-col gap-1.5">
            <p className="text-sm leading-[18px] font-semibold break-words text-mb-ink">
              {LIVE_POLL_COPY.numberedQuestion(question.position + 1, question.text)}
            </p>
            {topAnswers(question).map(top => (
              <div
                key={top.id}
                className="relative flex min-h-10 items-center justify-between gap-2 overflow-hidden rounded-[10px] border border-mb-border bg-white px-3"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 bg-mb-teal-light"
                  style={{ width: `${Math.min(100, Math.max(0, top.share))}%` }}
                />
                <span className="relative min-w-0 flex-1 py-1 text-[15px] leading-[18px] break-words text-mb-ink">
                  {top.text}
                </span>
                <span className="relative shrink-0 text-sm leading-[18px] font-semibold text-mb-ink">
                  {LIVE_POLL_COPY.share(top.share)}
                </span>
              </div>
            ))}
          </li>
        ))}
      </ul>
      {paging ? (
        <div
          className="flex items-center justify-between gap-2 rounded-xl border border-mb-border bg-white py-[9px] pr-2 pl-3"
          data-testid="host-paging"
        >
          <span className="text-sm leading-[18px] text-mb-gray-dark">
            {LIVE_HOST_COPY.pageOf(shown.page + 1, shown.pages)}
          </span>
          <span className="flex gap-2">
            <PagingButton
              disabled={busy}
              onClick={() => {
                onPage(shown.paused ? 'resume' : 'pause');
              }}
            >
              {shown.paused ? (
                <Play className="size-3.5 fill-mb-ink" strokeWidth={0} aria-hidden="true" />
              ) : (
                <PauseGlyph />
              )}
              {shown.paused ? LIVE_POLL_COPY.resumePages : LIVE_POLL_COPY.pausePages}
            </PagingButton>
            <PagingButton
              disabled={busy}
              onClick={() => {
                onPage('next');
              }}
            >
              {LIVE_POLL_COPY.nextPage}
              <ChevronRight className="size-3.5" strokeWidth={2.5} aria-hidden="true" />
            </PagingButton>
          </span>
        </div>
      ) : null}
      {showJoinsSummaryLine(host) ? (
        // gray-600 kept: no frame draws this line (live-H4: "No join-summary line"), so no mb grey to take.
        <p className="text-[13px] leading-[18px] text-gray-600">{LIVE_HOST_COPY.joinsSummary}</p>
      ) : null}
    </>
  );

  return (
    <HostLayout
      body={body}
      bodyClassName="gap-[18px]"
      testId="host-controls"
      size="large"
      bar={
        <>
          {back}
          <div className="flex items-center">
            <CopyScreenLink code={code} host={host} large />
          </div>
        </>
      }
    />
  );
};

/** "Pause pages" / "Resume pages" / "Next page" (H4): h36 drawn, a 44px hit area (L8). */
const PagingButton: React.FC<{
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ disabled, onClick, children }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="relative inline-flex h-9 items-center gap-1.5 rounded-full border border-mb-control-border px-2.5 text-sm leading-[18px] font-medium text-mb-ink before:absolute before:inset-x-0 before:-inset-y-1 before:content-[''] hover:bg-mb-bg disabled:cursor-progress disabled:opacity-60"
    data-hit="44"
  >
    {children}
  </button>
);

/** H4's pause glyph: two vertical lines (not the Lucide `pause` rectangles). */
const PauseGlyph: React.FC = () => (
  <svg viewBox="0 0 14 14" className="size-3.5" aria-hidden="true">
    <path d="M4.5 3v8M9.5 3v8" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" />
  </svg>
);

/** The one state chip style for "Not started" and "Ended" (RESOLUTIONS L5); "Live" is `LiveBadge`. */
export const StateChip: React.FC<{ label: string }> = ({ label }) => (
  <span className="inline-flex shrink-0 items-center rounded-full border border-mb-border px-2.5 py-1 text-xs leading-4 font-bold tracking-[0.08em] text-mb-dark-gray uppercase">
    {label}
  </span>
);

const EndedChip: React.FC = () => <StateChip label={LIVE_HOST_COPY.chipEnded} />;

// AIDEV-NOTE: N063 stays silent until it is on POLL_SENDS_ENABLED (TECH-867 § Views H4), and the client has no
// read of that flag: flip this when the summary sends. The line also hides once `joins_summary_sent` is true.
export const LIVE_JOINS_SUMMARY_SENDS = false;

/** H4's summary line: only while the summary would send and has not gone out (absent field: as drawn). */
// eslint-disable-next-line react-refresh/only-export-components -- the rule, read by its test
export const showJoinsSummaryLine = (
  host: Pick<LiveHostState, 'joins_summary_sent'>,
  sends: boolean = LIVE_JOINS_SUMMARY_SENDS,
): boolean => sends && host.joins_summary_sent !== true;
